import { MeasurementMessage } from '../Messages/types'
import {
  OVAL_CENTER_X,
  OVAL_CENTER_Y,
  OVAL_CENTER_Y_RATIO,
  OVAL_DESIGN_HEIGHT,
  OVAL_DESIGN_WIDTH,
  OVAL_PATH_HEIGHT,
  OVAL_PATH_WIDTH,
} from './assets/ovalPath'
import {
  MeasurementOvalNormalizedFrame,
  Oval,
  OvalLayout,
  OvalState,
} from './types'

/** Design-space Y of the oval path's top edge (center minus half the path height). */
const OVAL_PATH_TOP = OVAL_CENTER_Y - OVAL_PATH_HEIGHT / 2

const toOvalLayout = (
  width: number,
  height: number,
  scale: number,
  translateX: number,
  translateY: number
): OvalLayout => ({
  width,
  height,
  scale,
  transform: `translate(${translateX}, ${translateY}) scale(${scale})`,
  ovalTop: translateY + OVAL_PATH_TOP * scale,
})

/**
 * Maps the design-space oval onto a container of arbitrary size.
 * The oval is centered horizontally and keeps the design's relative
 * vertical position (slightly below center). `scaleMultiplier` allows
 * adjusting the oval size per device / camera resolution.
 */
export const getOvalLayout = (
  width: number,
  height: number,
  scaleMultiplier = 1
): OvalLayout => {
  const scale =
    Math.min(width / OVAL_DESIGN_WIDTH, height / OVAL_DESIGN_HEIGHT) *
    scaleMultiplier
  const translateX = width / 2 - OVAL_CENTER_X * scale
  const translateY = height * OVAL_CENTER_Y_RATIO - OVAL_CENTER_Y * scale
  return toOvalLayout(width, height, scale, translateX, translateY)
}

/**
 * Maps a normalized native camera-viewport frame onto this component's
 * container while preserving the design oval's aspect ratio.
 * `scaleMultiplier` grows or shrinks the oval around the frame's center.
 *
 * The frame is clipped to normalized viewport bounds. Invalid or empty frames
 * return `null`, allowing callers to retain the responsive fallback.
 */
export const getNormalizedOvalLayout = (
  width: number,
  height: number,
  frame: MeasurementOvalNormalizedFrame,
  scaleMultiplier = 1
): OvalLayout | null => {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    !Number.isFinite(frame.x) ||
    !Number.isFinite(frame.y) ||
    !Number.isFinite(frame.width) ||
    !Number.isFinite(frame.height) ||
    frame.width <= 0 ||
    frame.height <= 0
  ) {
    return null
  }

  const left = Math.max(0, Math.min(frame.x, 1))
  const top = Math.max(0, Math.min(frame.y, 1))
  const right = Math.max(0, Math.min(frame.x + frame.width, 1))
  const bottom = Math.max(0, Math.min(frame.y + frame.height, 1))
  const frameWidth = (right - left) * width
  const frameHeight = (bottom - top) * height
  if (frameWidth <= 0 || frameHeight <= 0) {
    return null
  }

  const scale =
    Math.min(frameWidth / OVAL_PATH_WIDTH, frameHeight / OVAL_PATH_HEIGHT) *
    scaleMultiplier
  if (!Number.isFinite(scale) || scale <= 0) {
    return null
  }

  const centerX = ((left + right) * width) / 2
  const centerY = ((top + bottom) * height) / 2
  const translateX = centerX - OVAL_CENTER_X * scale
  const translateY = centerY - OVAL_CENTER_Y * scale

  return toOvalLayout(width, height, scale, translateX, translateY)
}

/**
 * Camera-driven frame when present, otherwise the responsive fallback.
 * Returns `null` only when the container has no usable size.
 */
export const resolveOvalLayout = (
  width: number,
  height: number,
  normalizedFrame?: MeasurementOvalNormalizedFrame,
  scaleMultiplier = 1
): OvalLayout | null => {
  if (width <= 0 || height <= 0) {
    return null
  }
  const fallback = getOvalLayout(width, height, scaleMultiplier)
  if (!normalizedFrame) {
    return fallback
  }
  return (
    getNormalizedOvalLayout(
      width,
      height,
      normalizedFrame,
      scaleMultiplier
    ) ?? fallback
  )
}

/** True when the oval's top edge sits inside a top band of `bandHeight`. */
export const doesOvalOverlapTopBand = (
  layout: OvalLayout,
  bandHeight: number
): boolean => layout.ovalTop < bandHeight

const messageStates: Record<MeasurementMessage, OvalState> = {
  initial: Oval.States.initial,
  start: Oval.States.initial,
  noGlasses: Oval.States.initial,
  tooClose: Oval.States.warning,
  tooFar: Oval.States.warning,
  tooTilted: Oval.States.warning,
  headUp: Oval.States.warning,
  headDown: Oval.States.warning,
  headLeft: Oval.States.warning,
  headRight: Oval.States.warning,
  openEyes: Oval.States.warning,
  holdStill: Oval.States.scanning,
  scanning: Oval.States.scanning,
  analyzing: Oval.States.completed,
  completed: Oval.States.completed,
}

export const getOvalState = (message: MeasurementMessage | null): OvalState => {
  if (!message) {
    return Oval.States.initial
  }
  return messageStates[message]
}
